import React, { PureComponent } from 'react';
import DocumentTitle from 'react-document-title';
import { withTranslation } from 'react-i18next';

import { createAlert } from 'services/alert-service';
import { validateEmail } from 'utils/validators';

import { getSSOProviders } from 'api/users-api';

import { MeetingEmbedContext } from 'context/MeetingEmbedContext';

import FieldDefault from './field-default';
import GoogleLogin from './google-login';
import MicrosoftLogin from './MicrosoftLogin';

class Login extends PureComponent {
    static contextType = MeetingEmbedContext;

    static defaultProps = {
        email: ''
    };

    constructor(props) {
        super(props);

        this.state = {
            email: this.props.email,
            ssoEmail: '',
            password: '',
            loading: false,
            ssoLogin: false,
            ssoProviders: []
        }

        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    handleEmailChange = (value) => {
        this.setState({
            email: value
        });
    }

    handlePasswordChange = (value) => {
        this.setState({
            password: value
        });
    }

    handleSSOEmailChange = value => {
        this.setState({
            ssoEmail: value
        });
    }

    handleSuccessClick = () => {
        this.submit()
    }

    handleSSOSubmit = async () => {
        const { t } = this.props;

        this.setState({ ssoLoading: true });

        const providers = await getSSOProviders(this.state.ssoEmail);
        if (!providers?.length) {
            createAlert('error', t('alerts.noSSOForEmail'));
        } else {
            this.setState({ ssoProviders: providers });
        }

        this.setState({ ssoLoading: false });
    }

    handleForgottenPasswordClick = () => {
        this.props.onForgottenPasswordClick(this.state.email);
    }

    handleSignupClick = () => {
        this.props.onSignupClick(this.state.email);
    }

    submit() {
        this.setState({
            loading: true
        });

        this.props.onSuccess && this.props.onSuccess({
            email: this.state.email,
            password: this.state.password
        })
            .catch(() => {
                this.setState({
                    loading: false
                });
            });
    }

    handleKeyDown(event) {
        if (event.which === 13) {
            this.submit();
        }
    }

    handleLoginWithSSOClick = () => {
        this.setState({
            ssoLogin: true,
            ssoProviders: []
        });
    }

    handleSSOBack = () => {
        this.setState({
            ssoLogin: false
        });
    }

    get populated() {
        return validateEmail(this.state.email) && this.state.password.length >= 6;
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.email !== this.props.email && this.props.email !== this.state.email) {
            this.setState({
                email: this.props.email
            });
        }
    } 

    render() {
        const { t } = this.props;

        return (
            <DocumentTitle title={`${t('Login')} | Nifty`}>
                <div className="section-login">
                    <div className="section-head">
                        <h1>{ t('loginTitle') }</h1>
                    </div>

                    {
                        this.state.ssoLogin ? (
                            <React.Fragment>
                                <div className="section-body">
                                    <div className="form">
                                        {
                                            !this.state.ssoProviders.length ? (
                                                <div className="form-row">
                                                    <FieldDefault id="login-email" type="email" label={ t('common:Email Address') } value={this.state.ssoEmail} onChange={this.handleSSOEmailChange} required={true} placeholder="name@company.com" />
                                                </div>
                                            ) : null
                                        }

                                        {
                                            this.state.ssoProviders.includes('google') ? (
                                                <div className="form-row">
                                                    <GoogleLogin className="btn btn-link btn-text-caution" />
                                                </div>
                                            ) : null
                                        }

                                        {
                                            this.state.ssoProviders.includes('microsoft') ? (
                                                <div className="form-row">
                                                    <MicrosoftLogin className="btn btn-link btn-text-caution" />
                                                </div>
                                            ) : null
                                        }
                                    </div>
                                </div>

                                <div className="section-cta">
                                    <div className="btn-group btn-group-auto">
                                        <button className="btn btn-secondary" onClick={this.handleSSOBack}>Back</button>

                                        {
                                            !this.state.ssoProviders.length ? (
                                                <button className={'btn btn-success btn-with-loading' + (this.state.ssoLoading ? ' loading' : '')} disabled={!validateEmail(this.state.ssoEmail)} onClick={this.handleSSOSubmit}>Sign in with SSO</button>
                                            ) : null
                                        }
                                    </div>
                                </div>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div className="section-body">
                                    <div className="form">
                                        <div className="form-row">
                                            <FieldDefault id="login-email" type="email" label={ t('common:Email Address') } value={this.state.email} onChange={this.handleEmailChange} required={true} placeholder="name@company.com" />
                                        </div>

                                        <div className="form-row">
                                            <FieldDefault id="login-password" type="password" label={ t('common:Password') } value={this.state.password} onChange={this.handlePasswordChange} required={true} placeholder={ t('common:Password') }>
                                                <span className="field-helper" onClick={this.handleForgottenPasswordClick} tabIndex="-1">{ t('user:password.Forgot password') }</span>
                                            </FieldDefault>
                                        </div>
                                    </div>
                                </div>

                                <div className="section-foot">
                                    <button
                                        id="login-submit"
                                        className={'btn btn-block btn-success btn-has-loading' + (this.state.loading ? ' loading' : '')}
                                        disabled={!this.populated}
                                        onClick={this.handleSuccessClick}
                                    >{ t('Log in') }</button>
                                </div>

                                {
                                    this.context?.inApp ? null : (
                                        <React.Fragment>
                                            <div className="section-foot centered">
                                                <GoogleLogin className="btn btn-link btn-text-caution" />
                                            </div>

                                            <div className="section-foot centered">
                                                <button className="btn btn-link" onClick={this.handleLoginWithSSOClick}>Sign in with SSO</button>
                                            </div>
                                        </React.Fragment>
                                    )
                                }


                                {
                                    this.props.onSignupClick ? (
                                        <div className="section-cta">
                                            <div className="section-divider"><span>{ t('common:or').toUpperCase() }</span></div>

                                            <p>{ t('Don’t have an account') }</p>

                                            <button className="btn btn-secondary btn-text-caution" id="btn-signup" onClick={this.handleSignupClick}>{ t('Sign up for free') }</button>
                                        </div>
                                    ) : null
                                }
                            </React.Fragment>
                        )
                    }
                </div>
            </DocumentTitle>
        );
    }
}

export default withTranslation(['onboarding', 'user', 'common'])(Login);
